import { useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import type { DeskAttachmentInput } from '@his/shared';
import { describeError } from '@/lib/auth';
import { Alert, Button } from '@/components/ui/primitives';
import { ACCEPTED_DESK_FILES, formatBytes, uploadDeskFile, type DeskEndpoints } from './desk-api';

/**
 * Picks screenshots and uploads them before the form is submitted.
 *
 * Uploading on selection rather than on submit is what lets the form fail
 * safely: by the time somebody presses Send, the bytes are already in storage
 * and only a few short keys travel with the request. A slow upload also shows
 * itself while there is still something else to read on the screen.
 */
export function AttachmentPicker({
  endpoints,
  extra,
  value,
  onChange,
  max = 5,
  disabled,
}: {
  endpoints: DeskEndpoints;
  /** Fields the presign needs beyond the file, e.g. which hospital. */
  extra?: Record<string, unknown>;
  value: DeskAttachmentInput[];
  onChange: (files: DeskAttachmentInput[]) => void;
  max?: number;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    setBusy(true);
    try {
      const room = max - value.length;
      const chosen = Array.from(files).slice(0, room);
      const uploaded = await Promise.all(chosen.map((file) => uploadDeskFile(endpoints, file, extra)));
      onChange([...value, ...uploaded]);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        accept={ACCEPTED_DESK_FILES}
        onChange={(event) => void pick(event.target.files)}
      />

      <Button
        type="button"
        variant="secondary"
        size="sm"
        loading={busy}
        disabled={disabled || value.length >= max}
        onClick={() => input.current?.click()}
      >
        <Paperclip className="me-1.5 h-4 w-4" />
        {value.length ? `Add another (${value.length}/${max})` : 'Attach a screenshot'}
      </Button>

      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((file) => (
            <li
              key={file.fileKey}
              className="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1 text-xs"
            >
              <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{file.fileName}</span>
              <span className="shrink-0 text-muted-foreground">{formatBytes(file.sizeBytes)}</span>
              <button
                type="button"
                aria-label={`Remove ${file.fileName}`}
                className="shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => onChange(value.filter((f) => f.fileKey !== file.fileKey))}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <Alert variant="destructive">{error}</Alert>}
    </div>
  );
}
